import { useState, useMemo } from 'react';
import { X, Type, CheckCircle2, AlertCircle } from 'lucide-react';
import { applyCase, getCaseExample, detectCase } from '../../utils/caseConverter';

const CaseConverterModal = ({ isOpen, onClose, onConfirm, nodes, selectedNode }) => {
  const [caseType, setCaseType] = useState('snake');
  const [applyTo, setApplyTo] = useState('both'); // 'entities', 'fields', 'both'
  const [scope, setScope] = useState('all'); // 'all', 'selected'

  const caseOptions = [
    {
      id: 'snake',
      name: 'snake_case',
      description: 'Lowercase with underscores',
      example: 'user_profile_data',
      icon: '🐍',
      common: 'Most common in SQL databases'
    },
    {
      id: 'camel',
      name: 'camelCase',
      description: 'First word lowercase, rest capitalized',
      example: 'userProfileData',
      icon: '🐪',
      common: 'Common in JavaScript/TypeScript'
    },
    {
      id: 'pascal',
      name: 'PascalCase',
      description: 'All words capitalized',
      example: 'UserProfileData',
      icon: '🅿️',
      common: 'Common in C#, Java classes'
    },
    {
      id: 'kebab',
      name: 'kebab-case',
      description: 'Lowercase with hyphens',
      example: 'user-profile-data',
      icon: '🔗',
      common: 'Common in URLs and CSS'
    },
    {
      id: 'upper',
      name: 'UPPERCASE',
      description: 'All uppercase letters',
      example: 'USER_PROFILE_DATA',
      icon: '🔠',
      common: 'Common for constants'
    },
    {
      id: 'lower',
      name: 'lowercase',
      description: 'All lowercase letters',
      example: 'userprofiledata',
      icon: '🔡',
      common: 'Simple lowercase'
    }
  ];

  // Calculate preview
  const preview = useMemo(() => {
    if (!nodes || !Array.isArray(nodes)) return [];
    
    const affectedNodes = scope === 'all' ? nodes : (selectedNode ? [selectedNode] : []);
    const changes = [];

    affectedNodes.forEach(node => {
      const nodeChanges = {
        nodeId: node.id,
        originalName: node.data?.name || 'Unnamed',
        newName: node.data?.name || 'Unnamed',
        fields: []
      };

      // Convert entity name
      if (applyTo === 'entities' || applyTo === 'both') {
        nodeChanges.newName = applyCase(node.data?.name || 'Unnamed', caseType);
      }

      // Convert field names
      if (applyTo === 'fields' || applyTo === 'both') {
        (node.data?.fields || []).forEach(field => {
          if (!field || !field.name) return;
          const newName = applyCase(field.name, caseType);
          if (newName !== field.name) {
            nodeChanges.fields.push({
              original: field.name,
              new: newName,
              currentCase: detectCase(field.name)
            });
          }
        });
      }

      // Only add if there are changes
      if (nodeChanges.newName !== nodeChanges.originalName || nodeChanges.fields.length > 0) {
        changes.push(nodeChanges);
      }
    });

    return changes;
  }, [nodes, selectedNode, scope, applyTo, caseType]);

  const handleConfirm = () => {
    if (!preview || preview.length === 0) {
      return;
    }
    onConfirm({ caseType, applyTo, scope, preview });
  };

  const totalChanges = (preview || []).reduce((sum, node) => {
    let count = 0;
    if (node.newName !== node.originalName) count++;
    count += (node.fields || []).length;
    return sum + count;
  }, 0);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Type size={24} className="text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Convert Naming Convention
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                Apply consistent naming style to entities and fields
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Case Selection */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Select Naming Convention
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {caseOptions.map((option) => (
                <button
                  key={option.id}
                  onClick={() => setCaseType(option.id)}
                  className={`text-left p-4 rounded-lg border-2 transition-all ${
                    caseType === option.id
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-700'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{option.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 dark:text-white mb-1">
                        {option.name}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                        {option.description}
                      </div>
                      <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-purple-600 dark:text-purple-400">
                        {option.example}
                      </code>
                      <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        {option.common}
                      </div>
                    </div>
                    {caseType === option.id && (
                      <CheckCircle2 size={20} className="text-purple-600 dark:text-purple-400 flex-shrink-0" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Apply To Selection */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Apply To
            </h3>
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => setApplyTo('entities')}
                className={`p-3 rounded-lg border-2 transition-all ${
                  applyTo === 'entities'
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-purple-300'
                }`}
              >
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  Entity Names Only
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Convert table/collection names
                </div>
              </button>
              <button
                onClick={() => setApplyTo('fields')}
                className={`p-3 rounded-lg border-2 transition-all ${
                  applyTo === 'fields'
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-purple-300'
                }`}
              >
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  Field Names Only
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Convert column/field names
                </div>
              </button>
              <button
                onClick={() => setApplyTo('both')}
                className={`p-3 rounded-lg border-2 transition-all ${
                  applyTo === 'both'
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-purple-300'
                }`}
              >
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  Both
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Convert entities and fields
                </div>
              </button>
            </div>
          </div>

          {/* Scope Selection */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Scope
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setScope('all')}
                className={`p-3 rounded-lg border-2 transition-all ${
                  scope === 'all'
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-purple-300'
                }`}
              >
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  All Entities ({nodes?.length || 0})
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Apply to entire model
                </div>
              </button>
              <button
                onClick={() => setScope('selected')}
                disabled={!selectedNode}
                className={`p-3 rounded-lg border-2 transition-all ${
                  scope === 'selected'
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-purple-300'
                } ${!selectedNode ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  Selected Entity Only
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {selectedNode ? selectedNode.data?.name : 'No entity selected'}
                </div>
              </button>
            </div>
          </div>

          {/* Preview */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
              <AlertCircle size={16} />
              Preview Changes ({totalChanges} {totalChanges === 1 ? 'change' : 'changes'})
            </h3>
            
            {!preview || preview.length === 0 ? (
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-center text-gray-600 dark:text-gray-400 text-sm">
                No changes to preview. All names already match the selected convention.
              </div>
            ) : (
              <div className="space-y-3 max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-700/50">
                {(preview || []).map((change, index) => (
                  <div key={change.nodeId} className="space-y-2">
                    {/* Entity name change */}
                    {change.newName !== change.originalName && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-medium text-blue-600 dark:text-blue-400">Entity:</span>
                        <span className="text-gray-600 dark:text-gray-400 line-through">
                          {change.originalName}
                        </span>
                        <span className="text-gray-400">→</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {change.newName}
                        </span>
                      </div>
                    )}
                    
                    {/* Field changes */}
                    {change.fields && change.fields.length > 0 && (
                      <div className="ml-4 space-y-1">
                        {(change.fields || []).map((field, fIndex) => (
                          <div key={fIndex} className="flex items-center gap-2 text-xs">
                            <span className="text-gray-500 dark:text-gray-500">Field:</span>
                            <span className="text-gray-600 dark:text-gray-400 line-through">
                              {field.original}
                            </span>
                            <span className="text-gray-400">→</span>
                            <span className="text-gray-900 dark:text-white">
                              {field.new}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {preview && index < preview.length - 1 && (
                      <div className="border-t border-gray-200 dark:border-gray-600 pt-2" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Warning */}
          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <div className="flex gap-3">
              <AlertCircle size={20} className="text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  Warning: This action cannot be undone
                </p>
                <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                  All affected entity and field names will be converted. Make sure to review the preview before confirming.
                  You can undo by not saving the model.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center gap-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {totalChanges > 0 && (
              <span>
                Ready to apply <strong className="text-gray-900 dark:text-white">{totalChanges}</strong> {totalChanges === 1 ? 'change' : 'changes'}
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={totalChanges === 0}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                totalChanges === 0
                  ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
            >
              <CheckCircle2 size={16} />
              <span>Apply Changes</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseConverterModal;
